"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.mainMessage = void 0;
const lodash_1 = require("lodash");
const protocol_1 = require("./protocol");
const utils_1 = require("./utils");
/**
 * Manage sending messages for the main thread.
 */
class MainMessage {
    constructor() {
        // Current form marking.
        this.origin = 'main';
        /**
         * Widget Map loaded by the main thread, managing connection status and
         * view calculation subscriptions.
         * key => widgetId
         */
        this.widgets = new Map;
        this.messageBridge = protocol_1.postMessage;
    }
    /**
     * Wraps the emit parameter with the current form identifier.
     * @param data
     */
    wrapEmitData(data) {
        return Object.assign(Object.assign({}, data), { origin: this.origin });
    }
    /**
     * Change connect status.
     */
    changeConnectStatus(widgetId, status) {
        const widget = this.widgets.get(widgetId);
        if (widget) {
            widget.connect = status;
        }
    }
    /**
     * @param widgetId
     * @returns
     */
    getConnectStatus(widgetId) {
        const widget = this.widgets.get(widgetId);
        return widget === null || widget === void 0 ? void 0 : widget.connect;
    }
    /**
     * Set widget window
     * @param widgetId
     * @param window
     * @returns
     */
    initWidgetWindow(widgetId, window, bindDatasheetId) {
        // SYN_SENT
        const widget = this.widgets.get(widgetId);
        // Parameter checking, sandbox type widget must be passed in window.
        if (!window) {
            throw new Error(`widget(${widgetId}) declare sandbox, you must pass it into window`);
        }
        if (widget) {
            console.log('Please do not connect the widget repeatedly.');
            return;
        }
        this.widgets.set(widgetId, {
            connect: protocol_1.ConnectStatus.SYN_SENT,
            subscribeViews: [],
            bindDatasheetId
        });
        this.messageBridge.addWindow(widgetId, window);
    }
    /**
     * Send messages.
     * @param widgetId
     * @param type
     * @param data
     */
    emit(widgetId, type, data, messageId) {
        this.messageBridge.emit(type, this.wrapEmitData({ success: true, data: data }), widgetId, messageId);
    }
    /**
     * Broadcast messages.
     * @param type
     * @param data
     */
    emitBroadcast(type, data) {
        this.messageBridge.emit(type, this.wrapEmitData({ success: true, data: data }));
    }
    /**
     * Receiving messages.
     * @param widgetId
     * @param type
     * @param callback
     */
    on(widgetId, type, callback) {
        this.messageBridge.on(type, (0, utils_1.interceptor)(callback), widgetId, widgetId);
    }
    /**
     * Clear the relevant data when the widget is uninstalled.
     * @param widgetId
     */
    unMounted(widgetId) {
        const widget = this.widgets.get(widgetId);
        if (!widget) {
            return;
        }
        this.widgets.delete(widgetId);
        this.messageBridge.removeWindow(widgetId);
        this.messageBridge.removeListenEventByKey(widgetId);
    }
    /**
     * Remove the message listener, clear the entire message type listener without specifying the window listener.
     * @param widgetId
     * @param type message type
     * @param key window ID
     */
    removeListenEvent(widgetId, type) {
        const widget = this.widgets.get(widgetId);
        if (!widget) {
            return;
        }
        this.messageBridge.removeListenEvent(type, widgetId);
    }
    /**
     * Get the view of all widget in the main thread or a subscribed widget.
     * @param widgetId
     */
    subscribeViews(widgetId) {
        var _a;
        if (widgetId) {
            return ((_a = this.widgets.get(widgetId)) === null || _a === void 0 ? void 0 : _a.subscribeViews) || [];
        }
        return [...this.widgets.values()].reduce((collected, item) => {
            collected.push(...item.subscribeViews);
            return collected;
        }, []);
    }
    /**
     * Receive connect a widget.
     * @param widgetId
     * @param callback
     * @param window
     */
    onConnectWidget(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_CONNECT, () => {
            callback(protocol_1.ConnectStatus.SYN_SENT);
            this.emit(widgetId, protocol_1.MessageType.MAIN_CONNECT_WIDGET, { origin });
            this.on(widgetId, protocol_1.MessageType.WIDGET_CONNECT_ES, () => {
                // ESTABLISHED
                this.changeConnectStatus(widgetId, protocol_1.ConnectStatus.ESTABLISHED);
                callback(protocol_1.ConnectStatus.ESTABLISHED);
            });
        });
    }
    /**
     * Listening to widget initialization and sending the initialization data required by the widget.
     * @param callback
     */
    initWidget(widgetId, data) {
        this.emit(widgetId, protocol_1.MessageType.MAIN_INIT_WIDGET_DATA, data);
    }
    /**
     * Sync widget config.
     * @param widgetId
     * @param config
     */
    syncWidgetConfig(widgetId, config) {
        this.emit(widgetId, protocol_1.MessageType.MAIN_SYNC_WIDGET_CONFIG, config);
    }
    /**
     * @param widgetId
     */
    refreshWidget(widgetId) {
        this.emit(widgetId, protocol_1.MessageType.MAIN_REFRESH_WIDGET);
    }
    /**
     * Listening for widget config sync from widgets.
     * @param widgetId
     * @param callback
     */
    onWidgetConfig(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_SYNC_WIDGET_CONFIG, callback);
    }
    /**
     * Listen to the cmd from the widget synced to the application.
     */
    onSyncCmdOptions(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_SYNC_COMMAND, callback);
    }
    /**
     * Synchronize the results of the main application application cmd.
     */
    syncCmdOptionsResult(widgetId, cmdOptionsResult) {
        this.emit(widgetId, protocol_1.MessageType.MAIN_SYNC_COMMAND_RESULT, cmdOptionsResult);
    }
    /**
     * Synchronized RecordPicker selection results.
     */
    syncRecordPickerResult(widgetId, recordIds, messageId) {
        this.emit(widgetId, protocol_1.MessageType.MAIN_SYNC_RECORD_PICKER_RESULT, recordIds, messageId);
    }
    /**
     * Listening for expand card requests from the widget.
     */
    onExpandRecord(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_EXPAND_RECORD, callback);
    }
    /**
     * Listens for requests to expand the record selector from the widget.
     */
    onExpandRecordPicker(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_EXPAND_RECORD_PICKER, callback);
    }
    /**
     * Mouse in and mouse out widgets iframe.
     */
    onMouseListener(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_MOUSE_EVENT, callback);
    }
    /**
     * Enter developer mode.
     */
    onExpandDevConfig(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_EXPAND_DEV_CONFIG, callback);
    }
    /**
     * Broadcast action.
     * @param action
     */
    syncActionBroadcast(action) {
        this.emitBroadcast(protocol_1.MessageType.MAIN_SYNC_ACTION, action);
    }
    /**
     * Synchronize the action to the specified widget.
     * @param widgetId
     * @param action
     */
    syncAction(widgetId, action, messageId) {
        this.emit(widgetId, protocol_1.MessageType.MAIN_SYNC_ACTION, action, messageId);
    }
    onFetchDatasheet(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_FETCH_DATASHEET, callback);
    }
    /**
     * Synchronizing view subscription data.
     * @param widgetId
     * @param callback Pick out the new subscriptions, the user determines if there is currently a view-derived cache,
     * and if not, go to dispatch to trigger the computation.
     */
    onSyncWidgetSubscribeView(widgetId, callback) {
        this.on(widgetId, protocol_1.MessageType.WIDGET_SUBSCRIBE_CHANGE, (subscribeViews) => {
            const widget = this.widgets.get(widgetId);
            if (widget) {
                const currentSubscribeViews = widget.subscribeViews;
                this.widgets.set(widgetId, Object.assign(Object.assign({}, widget), { subscribeViews }));
                const newSubscribeViews = (0, lodash_1.differenceBy)(subscribeViews, currentSubscribeViews, ({ datasheetId, viewId }) => `${datasheetId}-${viewId}`);
                callback(newSubscribeViews);
            }
        });
    }
}
exports.mainMessage = new MainMessage();
